import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Container,
  Fade,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Dashboard as DashboardIcon,
  Code as CodeIcon,
  Timeline as MonitoringIcon,
  Speed as BenchmarkIcon,
  Security as SecurityIcon,
  PlayArrow as PlaygroundIcon,
  Api as ApiIcon,
  Circle as StatusIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useApp } from '../context/AppContext';

const ModernLayout = ({ children, currentTab, onTabChange, systemStatus, onThemeToggle, currentTheme, onLanguageToggle, currentLanguage }) => {
  const { websocketConnected } = useApp();
  const { t } = useTranslation();

  const getStatusColor = () => {
    if (!websocketConnected) return 'error';
    if (systemStatus?.data?.status === 'healthy') return 'success';
    return 'warning';
  };

  const getStatusText = () => {
    if (!websocketConnected) return t('disconnected');
    if (systemStatus?.data?.status === 'healthy') return t('online');
    return t('warning');
  };

  const tabs = [
    { label: t('overview'), value: 'dashboard', icon: <DashboardIcon /> },
    { label: t('sandbox'), value: 'sandbox', icon: <CodeIcon /> },
    { label: t('monitoring'), value: 'monitoring', icon: <MonitoringIcon /> },
    { label: t('benchmarks'), value: 'benchmarks', icon: <BenchmarkIcon /> },
    { label: t('security'), value: 'security', icon: <SecurityIcon /> },
    { label: t('api_playground'), value: 'playground', icon: <PlaygroundIcon /> },
    { label: t('api_docs'), value: 'api', icon: <ApiIcon /> },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'background.default',
    }}>
      {/* Modern Top Navigation */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          background: (theme) => theme.palette.mode === 'dark' 
            ? 'rgba(15, 15, 35, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: (theme) => theme.palette.mode === 'dark' 
            ? '1px solid rgba(255, 255, 255, 0.1)' 
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 0.3, sm: 3 }, minHeight: { xs: 48, sm: 64 } }}>
          {/* Logo and Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, sm: 2 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.3, sm: 1 },
              background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
              borderRadius: { xs: 1, sm: 2 },
              px: { xs: 0.5, sm: 2 },
              py: { xs: 0.3, sm: 1 },
            }}>
              <CodeIcon sx={{ color: 'white', fontSize: { xs: '1rem', sm: '1.5rem' } }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  fontSize: { xs: '0.65rem', sm: '1.1rem' },
                  whiteSpace: 'nowrap',
                }}
              >
                DevSandbox Pro
              </Typography>
            </Box>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.7)' 
                  : 'rgba(0, 0, 0, 0.6)',
                fontWeight: 300,
                display: { xs: 'none', lg: 'block' },
                fontSize: { md: '0.8rem', lg: '0.875rem' },
              }}
            >
              {t('containerized_development_environment')}
            </Typography>
          </Box>

          {/* Status and Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, sm: 2 } }}>
            <Tooltip title={`System Status: ${getStatusText()}`}>
              <Chip
                icon={<StatusIcon />}
                label={getStatusText()}
                color={getStatusColor()}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 3,
                  fontSize: { xs: '0.6rem', sm: '0.8125rem' },
                  height: { xs: 24, sm: 32 },
                  '& .MuiChip-icon': {
                    animation: websocketConnected ? 'pulse 2s infinite' : 'none',
                    fontSize: { xs: '0.7rem', sm: '1rem' },
                  },
                  '& .MuiChip-label': {
                    px: { xs: 0.3, sm: 1 },
                  },
                }}
              />
            </Tooltip>

            <Tooltip title={t('toggle_theme')}>
              <IconButton 
                color="inherit"
                onClick={onThemeToggle}
                size="small"
                sx={{ p: { xs: 0.3, sm: 1 } }}
              >
                {currentTheme === 'dark' ? 
                  <LightModeIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.5rem' } }} /> : 
                  <DarkModeIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.5rem' } }} />
                }
              </IconButton>
            </Tooltip>

            <Tooltip title={t('toggle_language')}>
              <IconButton 
                color="inherit"
                onClick={onLanguageToggle}
                size="small"
                sx={{ p: { xs: 0.3, sm: 1 } }}
              >
                <LanguageIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.5rem' } }} />
                <Typography variant="caption" sx={{ ml: { xs: 0.1, sm: 0.5 }, fontSize: { xs: '0.5rem', sm: '0.7rem' } }}>
                  {currentLanguage.toUpperCase()}
                </Typography>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>

        {/* Navigation Tabs */}
        <Box sx={{ 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          px: { xs: 1, sm: 3 },
        }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => onTabChange(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 60,
                textTransform: 'none',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-selected': {
                  color: '#667eea',
                },
              },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tab.icon}
                    {tab.label}
                  </Box>
                }
                value={tab.value}
              />
            ))}
          </Tabs>
        </Box>
      </AppBar>

      {/* Main Content Area */}
      <Container 
        maxWidth={false} 
        sx={{ 
          py: { xs: 2, sm: 3 }, 
          px: { xs: 1, sm: 3 },
          minHeight: 'calc(100vh - 140px)',
          backgroundColor: 'background.default',
        }}
      >
        <Fade in={true} timeout={300}>
          <Box>
            {children}
          </Box>
        </Fade>
      </Container>

      {/* Global Styles for Animations */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default ModernLayout;
